import { EvaChart } from "constants/charts.enums";
import { EntityType } from "constants/entities.enum";

import { generateChartEntity } from "./chart";
import {
  generateAverageFeatureEntity,
  generateBestFitFeatureEntity,
  generateCalculationMethodFeatureEntity,
  generateChartFocusEntity,
  generateChartTypeParamFeature,
  generateChartsFeatureEntity,
  generateCutOffFeatureEntity,
  generateDataRangeSelectFeatureEntity,
  generateDataTableFeatureEntity,
  generateHighlightedLineOpacityFeatureEntity,
  generateHighlightedLineSizeFeatureEntity,
  generateHistogramToggleFeatureEntity,
  generateJitterPlotFeatureEntity,
  generateLassoFeatureEntity,
  generateLineOpacityFeatureEntity,
  generateLineSizeFeatureEntity,
  generateLogAxisFeature,
  generateMidstreamChartFocusEntity,
  generateMovingAverageDaysFeatureEntity,
  generateMultiProductFeatureEntity,
  generateMultiProductFocusFeatureEntity,
  generateNormalizationFeatureEntity,
  generateP10FeatureEntity,
  generateP50FeatureEntity,
  generateP90FeatureEntity,
  generatePointOpacityFeatureEntity,
  generatePointSizeFeatureEntity,
  generatePressureChartSettingsFeature,
  generateScreenshotFeatureEntity,
  generateShutInMonthsFeatureEntity,
  generateSlopeLineFeatureEntity,
  generateSourceFeatureEntity,
  generateTimeStepFeatureEntity,
  generateTotalFluidChartSettingsFeature,
  generateViolinPlotFeatureEntity,
  generateXAxisSortFeatureEntity
} from "./eva/features/feature";
import { generateBoxPlotRequestBody } from "./eva/requests/boxPlot";
import { generateBoxPlotMidstreamRequestBody } from "./eva/requests/boxPlotMidstream";
import { generateCAGRRequestBody } from "./eva/requests/cagr";
import { generateCrossPlotRequestBody } from "./eva/requests/crossPlot";
import { generateCrossPlotMidstreamRequestBody } from "./eva/requests/crossPlotMidstream";
import { generateCumTimeRequestBody } from "./eva/requests/cumTime";
import { generateDeclineRateRequestBody } from "./eva/requests/declineRate";
import { generateInjectorTotalRequestBody } from "./eva/requests/injectorTotal";
import { generateMaterialBalanceTimeRequestBody } from "./eva/requests/materialBalanceTime";
import { generateMosaicRequestBody } from "./eva/requests/mosaic";
import { generateMosaicMidstreamRequestBody } from "./eva/requests/mosaicMidstream";
import { generatePieRequestBody } from "./eva/requests/pie";
import { generateProbitRequestBody } from "./eva/requests/probit";
import { generateRateCumRequestBody } from "./eva/requests/rateCum";
import { generateRateDateRequestBody } from "./eva/requests/rateDate";
import { generateRateDateMidstreamRequestBody } from "./eva/requests/rateDateMidstream";
import { generateRateTimeRequestBody } from "./eva/requests/rateTime";
import { generateRecoveryPVIRequestBody } from "./eva/requests/recoveryPVI";
import { generateRecoveryTimeRequestBody } from "./eva/requests/recoveryTime";
import { generateRigFracActivityRequestBody } from "./eva/requests/rigFracActivity";
import { generateRigFracRecentActivityRequestBody } from "./eva/requests/rigFracRecentActivity";
import { generateStackedBarRequestBody } from "./eva/requests/stackedBar";
import { generateStackedBarHorizontalRequestBody } from "./eva/requests/stackedBarHorizontal";
import { generateStackedBarMidstreamRequestBody } from "./eva/requests/stackedBarMidstream";
import { generateTotalRateCumRequestBody } from "./eva/requests/totalRateCum";
import { generateTotalRateDateRequestBody } from "./eva/requests/totalRateDate";
import { generateTotalRateDateMidstreamRequestBody } from "./eva/requests/totalRateDateMidstream";
import { generateTrendDateRequestBody } from "./eva/requests/trendDate";
import { generateTrendDateMidstreamRequestBody } from "./eva/requests/trendDateMidstream";
import { generateWellContributionRequestBody } from "./eva/requests/wellContribution";
import { generateWellStatusCurrentRequestBody } from "./eva/requests/wellStatusCurrent";
import { generateWellStatusOverTimeRequestBody } from "./eva/requests/wellStatusOverTime";
import {
  ChartOption,
  generateBarSeriesOptions,
  generateBoxPlotSeriesOptions,
  generateLineSeriesOptions,
  generateMosaicSeriesOptions,
  generatePieSeriesOptions,
  generateScatterSeriesOptions,
  generateTitleOptions
} from "./options";
import { generateChartOptions } from "./options/chart";
import { generateXAxisOptions } from "./options/xAxis";
import { generateXAxisCategoryOptions } from "./options/xAxisCatagorys";
import { generateYAxisOptions } from "./options/yAxis";

const GENERATORS = {
  [EntityType.Chart]: generateChartEntity,
  [EntityType.ChartsFeature]: generateChartsFeatureEntity,
  [EntityType.ChartDataTableFeature]: generateDataTableFeatureEntity,
  [EntityType.SourceFeature]: generateSourceFeatureEntity,
  [EntityType.TimeStepFeature]: generateTimeStepFeatureEntity,
  [EntityType.CutOffChartFeature]: generateCutOffFeatureEntity,
  [EntityType.ScreenshotFeature]: generateScreenshotFeatureEntity,
  [EntityType.ShutInMonthsChartFeature]: generateShutInMonthsFeatureEntity,
  [EntityType.MovingAverageDaysChartFeature]: generateMovingAverageDaysFeatureEntity,
  [EntityType.LassoFeature]: generateLassoFeatureEntity,
  [EntityType.P10Feature]: generateP10FeatureEntity,
  [EntityType.P50Feature]: generateP50FeatureEntity,
  [EntityType.P90Feature]: generateP90FeatureEntity,
  [EntityType.AverageFeature]: generateAverageFeatureEntity,
  [EntityType.NormalizationFeature]: generateNormalizationFeatureEntity,
  [EntityType.ChartFocusFeature]: generateChartFocusEntity,
  [EntityType.MultiProductFocusFeature]: generateMultiProductFocusFeatureEntity,
  [EntityType.PointOpacityFeature]: generatePointOpacityFeatureEntity,
  [EntityType.PointSizeFeature]: generatePointSizeFeatureEntity,
  [EntityType.LineSizeFeature]: generateLineSizeFeatureEntity,
  [EntityType.LineOpacityFeature]: generateLineOpacityFeatureEntity,
  [EntityType.HighlightedLineSizeFeature]: generateHighlightedLineSizeFeatureEntity,
  [EntityType.HighlightedLineOpacityFeature]: generateHighlightedLineOpacityFeatureEntity,
  [EntityType.ChartTypeParamFeature]: generateChartTypeParamFeature,
  [EntityType.HistogramToggleFeature]: generateHistogramToggleFeatureEntity,
  [EntityType.JitterPlotToggleFeature]: generateJitterPlotFeatureEntity,
  [EntityType.ViolinPlotToggleFeature]: generateViolinPlotFeatureEntity,
  [EntityType.DataRangeSelectFeature]: generateDataRangeSelectFeatureEntity,
  [EntityType.BestFitToggleFeature]: generateBestFitFeatureEntity,
  [EntityType.CalculationMethodFeature]: generateCalculationMethodFeatureEntity,
  [EntityType.SlopeLineFeature]: generateSlopeLineFeatureEntity,

  [EntityType.ChartFocusMidStreamFeature]: generateMidstreamChartFocusEntity,
  [EntityType.PressureChartSettingsFeature]: generatePressureChartSettingsFeature,
  [EntityType.TotalFluidChartSettingsFeature]: generateTotalFluidChartSettingsFeature,
  [EntityType.LogAxisFeature]: generateLogAxisFeature,
  [EntityType.XAxisSortFeature]: generateXAxisSortFeatureEntity,
  [EntityType.MultiProductFeature]: generateMultiProductFeatureEntity,
  [EvaChart.RateCum]: generateRateCumRequestBody,
  [EvaChart.RecoveryPVI]: generateRecoveryPVIRequestBody,
  [EvaChart.RecoveryTime]: generateRecoveryTimeRequestBody,
  [EvaChart.CrossPlot]: generateCrossPlotRequestBody,
  [EvaChart.CumTime]: generateCumTimeRequestBody,
  [EvaChart.RateTime]: generateRateTimeRequestBody,
  [EvaChart.Pie]: generatePieRequestBody,
  [EvaChart.RateDate]: generateRateDateRequestBody,
  [EvaChart.TotalRateDate]: generateTotalRateDateRequestBody,
  [EvaChart.TotalRateCum]: generateTotalRateCumRequestBody,
  [EvaChart.DeclineRate]: generateDeclineRateRequestBody,
  [EvaChart.CAGR]: generateCAGRRequestBody,
  [EvaChart.TrendDate]: generateTrendDateRequestBody,
  [EvaChart.Mosaic]: generateMosaicRequestBody,
  [EvaChart.StackedBar]: generateStackedBarRequestBody,
  [EvaChart.StackedBarHorizontal]: generateStackedBarHorizontalRequestBody,
  [EvaChart.BoxPlot]: generateBoxPlotRequestBody,
  [EvaChart.Probit]: generateProbitRequestBody,
  [EvaChart.MaterialBalanceTime]: generateMaterialBalanceTimeRequestBody,
  [EvaChart.InjectorTotal]: generateInjectorTotalRequestBody,
  [EvaChart.RigFracActivity]: generateRigFracActivityRequestBody,
  [EvaChart.RigFracRecentActivity]: generateRigFracRecentActivityRequestBody,
  [EvaChart.WellStatusOverTime]: generateWellStatusOverTimeRequestBody,
  [EvaChart.WellStatusCurrent]: generateWellStatusCurrentRequestBody,

  // midstream charts
  [EvaChart.RateDateMidstream]: generateRateDateMidstreamRequestBody,
  [EvaChart.TotalRateDateMidstream]: generateTotalRateDateMidstreamRequestBody,
  [EvaChart.TrendDateMidstream]: generateTrendDateMidstreamRequestBody,
  [EvaChart.CrossPlotMidstream]: generateCrossPlotMidstreamRequestBody,
  [EvaChart.WellContribution]: generateWellContributionRequestBody,
  [EvaChart.MosaicMidstream]: generateMosaicMidstreamRequestBody,
  [EvaChart.StackedBarMidstream]: generateStackedBarMidstreamRequestBody,
  [EvaChart.BoxPlotMidstream]: generateBoxPlotMidstreamRequestBody,

  [ChartOption.Chart]: generateChartOptions,

  [ChartOption.XAxis]: generateXAxisOptions,
  [ChartOption.XAxisCategory]: generateXAxisCategoryOptions,
  [ChartOption.YAxis]: generateYAxisOptions,
  [ChartOption.Title]: generateTitleOptions,

  [ChartOption.LineSeries]: generateLineSeriesOptions,
  [ChartOption.ScatterSeries]: generateScatterSeriesOptions,
  [ChartOption.Pie]: generatePieSeriesOptions,
  [ChartOption.Box]: generateBoxPlotSeriesOptions,
  [ChartOption.MosaicSeries]: generateMosaicSeriesOptions,
  [ChartOption.Bar]: generateBarSeriesOptions
};

export type Generators = keyof typeof GENERATORS;

export function createEntityState<K extends Generators>(
  type: K,
  state: Partial<ReturnType<(typeof GENERATORS)[K]>>
): ReturnType<(typeof GENERATORS)[K]> {
  const generator = GENERATORS[type];
  if (!generator) {
    throw new Error(`Unknown entity type: ${type} to create state`);
  }
  return generator(state) as ReturnType<(typeof GENERATORS)[K]>;
}
